/**
 * Detection thresholds modal.
 *
 * Wraps the existing `DataQualityControls` panel in a reactstrap Modal so
 * the per-rule warn/critical pairs and the gap-detection toggle live one
 * click away from the grid rather than as a permanent panel above it.
 *
 * Changes apply live as the user edits — `onChange` propagates each tweak
 * to the parent so dots reflow without needing an explicit "Save" step.
 * The modal's only action is "Done" (dismiss) plus a "Reset to defaults"
 * escape hatch.
 */

import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { RotateCcw } from 'lucide-react';
import {
  DEFAULT_THRESHOLDS,
  type DetectionThresholds,
} from './dataQualityDetection';
import { DataQualityControls } from './DataQualityControls';

interface DataQualityControlsModalProps {
  isOpen: boolean;
  onClose: () => void;
  thresholds: DetectionThresholds;
  onChange: (next: DetectionThresholds) => void;
  gapDetectionEnabled: boolean;
  onGapDetectionChange: (enabled: boolean) => void;
}

export const DataQualityControlsModal = ({
  isOpen,
  onClose,
  thresholds,
  onChange,
  gapDetectionEnabled,
  onGapDetectionChange,
}: DataQualityControlsModalProps) => {
  const handleReset = () => {
    onChange(DEFAULT_THRESHOLDS);
    onGapDetectionChange(true);
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size='lg' scrollable>
      <ModalHeader toggle={onClose}>Detection thresholds</ModalHeader>
      <ModalBody>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-4'>
          Tune how the data-quality grid flags potential issues. Changes apply immediately.
        </p>
        <DataQualityControls
          thresholds={thresholds}
          onChange={onChange}
          gapDetectionEnabled={gapDetectionEnabled}
          onGapDetectionChange={onGapDetectionChange}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant='ghost' size='sm' onClick={handleReset}>
          <RotateCcw className='tw:size-3.5 tw:mr-1' />
          Reset to defaults
        </Button>
        <Button onClick={onClose}>Done</Button>
      </ModalFooter>
    </Modal>
  );
};
